<template>
  <Menu as="div" class="relative inline-block text-left">
    <div>
      <MenuButton
        class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100"
      >
        Actions
        <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute overflow-y-auto right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden overflow-auto"
      >
        <div class="py-1">
          <MenuItem v-slot="{ active }">
            <button
              @click="redirectToProductPage"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                View product
                <EyeIcon class="h-5 w-5 ml-2 text-primary-dark" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productApprovable" v-slot="{ active }">
            <button
              @click="approveProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Approve Product
                <CheckIcon class="h-5 w-5 ml-2 text-green-400" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productUnapprovable" v-slot="{ active }">
            <button
              @click="unApproveProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Unapprove Product
                <XIcon class="h-5 w-5 ml-2 text-red-400" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productMatchable" v-slot="{ active }">
            <button
              @click="matchProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Match Product
                <DocumentIcon class="h-5 w-5 ml-2 text-yellow-400" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productEnablable" v-slot="{ active }">
            <button
              @click="enableProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Enable Product
                <ArrowNarrowUpIcon class="h-5 w-5 ml-2 text-green-400" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productDisablable" v-slot="{ active }">
            <button
              @click="disableProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Disable Product
                <StopIcon class="h-5 w-5 ml-2 text-red-400" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
          <MenuItem v-if="productApprovable" v-slot="{ active }">
            <button
              @click="approveMatchPublishProducts(product.id)"
              :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm', 'w-full']"
            >
              <p class="flex">
                Approve, Match & Publish
                <ChevronDoubleRightIcon class="h-5 w-5 ml-2 text-primary-dark" aria-hidden="true" />
              </p>
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  ChevronDownIcon,
  EyeIcon,
  CheckIcon,
  XIcon,
  DocumentIcon,
  StopIcon,
  ArrowNarrowUpIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/vue/solid";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ChevronDownIcon,
    EyeIcon,
    CheckIcon,
    XIcon,
    DocumentIcon,
    StopIcon,
    ArrowNarrowUpIcon,
    ChevronDoubleRightIcon,
  },

  props: {
    product: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters("products", [
      "isProductApprovable",
      "isProductUnapprovable",
      "isProductMatchable",
      "isProductEnablable",
      "isProductDisablable",
    ]),

    productApprovable() {
      return this.isProductApprovable(this.product);
    },
    productUnapprovable() {
      return this.isProductUnapprovable(this.product);
    },
    productMatchable() {
      return this.isProductMatchable(this.product);
    },
    productEnablable() {
      return this.isProductEnablable(this.product);
    },
    productDisablable() {
      return this.isProductDisablable(this.product);
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),

    ...mapActions("products", [
      "approveProducts",
      "unApproveProducts",
      "matchProducts",
      "unmatchProducts",
      "enableProducts",
      "disableProducts",
      "approveMatchProducts",
      "approveMatchPublishProducts",
    ]),

    redirectToProductPage() {
      this.$router.push(`/products/${this.product.id}`);
    },
  },
};
</script>
